import React, { useEffect, useState } from 'react';
import { Mail, Pencil } from 'lucide-react';
import { getInitials } from '../../utils/stringUtils';
import { isPhoneLoginId } from '../../utils/phoneUtils';
import { COMMON_INPUT_CLASS } from '../../utils/constants';
import { updateStudentEmailByInstructor, describePhoneAuthError } from '../../services/auth';
import { useUI } from '../../context/AppContext';
import EmailSuggestion from '../ui/EmailSuggestion';
import { useConfirm } from '../../context/ConfirmContext';
import { normalizeEmail } from '../../utils/emailUtils';
import { suggestEmailFix } from '../../utils/emailSuggest';

const studentEmailOf = (student) => {
  if (student?.emailOrPhone && !isPhoneLoginId(student.emailOrPhone)) return student.emailOrPhone;
  return student?.email || '';
};

const StudentEmailEditor = ({ student }) => {
  const { showToast } = useUI();
  const currentEmail = studentEmailOf(student);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(currentEmail);
  const [saving, setSaving] = useState(false);
  const requestConfirm = useConfirm();

  useEffect(() => {
    setEditing(false);
    setDraft(currentEmail);
  }, [student?.id, currentEmail]);

  const unchanged = draft.trim().toLowerCase() === currentEmail.toLowerCase();

  const handleSave = async (e) => {
    e.preventDefault();
    if (saving || unchanged || !draft.trim()) return;
    const nextEmail = normalizeEmail(draft);
    const suggestion = suggestEmailFix(nextEmail);
    const ok = await requestConfirm({
      title: 'Öğrencinin e-postasını değiştir',
      tone: 'warning',
      step1Description: (
        <>
          <p>{student.name || 'Öğrenci'} için yeni e-posta adresini kontrol edin:</p>
          {currentEmail && (
            <p className="mt-2 text-slate-400 line-through break-all">{currentEmail}</p>
          )}
          <p className="mt-1 font-black text-slate-800 dark:text-white break-all">{nextEmail}</p>
          {suggestion && (
            <p className="mt-2 text-amber-700 dark:text-amber-300">
              Bunu mu demek istediniz: <strong className="break-all">{suggestion}</strong>?
            </p>
          )}
        </>
      ),
      step2Description: `Son onay: öğrenci bundan sonra yalnızca ${nextEmail} adresine gelen kodla giriş yapar; eski adres çalışmaz.`,
      confirmLabel: 'Evet, Değiştir',
    });
    if (!ok) return;
    setSaving(true);
    try {
      const email = await updateStudentEmailByInstructor(student.id, draft);
      setEditing(false);
      showToast(`E-posta güncellendi. ${student.name || 'Öğrenci'} artık ${email} ile giriş yapar.`);
    } catch (err) {
      showToast(describePhoneAuthError(err), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="relative z-10 mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-blue-600 dark:text-blue-400 hover:underline"
      >
        <Pencil size={13} /> {currentEmail ? 'E-postayı düzenle' : 'E-posta ekle'}
      </button>
    );
  }

  return (
    <form onSubmit={handleSave} className="relative z-10 mt-4 space-y-2 text-left">
      <input
        type="email"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="ornek@mail.com"
        disabled={saving}
        autoFocus
        className={COMMON_INPUT_CLASS}
        aria-label="Öğrencinin e-postası"
      />
      <EmailSuggestion email={draft} onApply={setDraft} />
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => { setEditing(false); setDraft(currentEmail); }}
          disabled={saving}
          className="flex-1 py-2.5 rounded-xl text-sm font-bold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 disabled:opacity-40"
        >
          Vazgeç
        </button>
        <button
          type="submit"
          disabled={saving || unchanged || !draft.trim()}
          className="flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40"
        >
          <Mail size={14} /> {saving ? 'Kaydediliyor…' : 'Kaydet'}
        </button>
      </div>
      <p className="text-[11px] font-medium text-slate-400 dark:text-slate-500">
        Öğrenci bundan sonra yeni adresine gelen kodla giriş yapar; eski adres çalışmaz.
      </p>
    </form>
  );
};

const StudentDetailSidebar = ({ syncStudent }) => (
  <div className="lg:col-span-1 space-y-6">
    <div className="bg-white dark:bg-slate-800 p-6 sm:p-8 rounded-3xl border border-slate-100 dark:border-slate-700 shadow-sm text-center relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-r from-blue-500 to-cyan-400 dark:from-blue-700 dark:to-cyan-700" />
      <div className="w-24 h-24 bg-white dark:bg-slate-800 rounded-full mx-auto flex items-center justify-center text-blue-600 dark:text-blue-400 text-3xl font-bold mb-4 shadow-lg border-4 border-white dark:border-slate-800 relative z-10 mt-6">
        {getInitials(syncStudent.name)}
      </div>
      <h2 className="text-xl font-bold text-slate-800 dark:text-white relative z-10">
        {syncStudent.name}
      </h2>
      <p className="text-slate-500 dark:text-slate-400 text-sm relative z-10 break-all">
        {syncStudent.emailOrPhone} • #{syncStudent.userCode}
      </p>
      {isPhoneLoginId(syncStudent.emailOrPhone) && syncStudent.email && (
        <p className="text-slate-500 dark:text-slate-400 text-sm relative z-10 break-all">{syncStudent.email}</p>
      )}
      <StudentEmailEditor student={syncStudent} />
    </div>
  </div>
);

export default StudentDetailSidebar;
